"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ArrowRightIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  GiftIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

const CONTENT = {
  fr: {
    h1: "Que deviennent vos meubles après un débarras ?",
    heroText:
      "Une question que beaucoup de clients se posent, souvent par attachement à ce qu'ils laissent derrière eux. Voici ce qui se passe concrètement, étape par étape, entre le moment où un meuble quitte le logement et sa destination finale.",
    freeQuote: "Demander un devis gratuit",
    intro:
      "Vider un logement soulève presque toujours la même question : qu'advient-il réellement de ce qui est évacué ? La réponse tient en trois mots — rachat, réemploi, recyclage — mais l'ordre dans lequel ces trois circuits interviennent compte autant que leur existence. Rien n'est chargé au hasard : chaque meuble et chaque objet est d'abord regardé, avant d'être orienté vers l'un de ces chemins plutôt qu'un autre. Ce guide détaille ce parcours, du premier tri sur place jusqu'à la part, réelle mais réduite, qui ne peut être ni rachetée ni valorisée.",
    s1: {
      title: "Premier tri sur place : ce qui peut être racheté",
      p1: "Avant de charger quoi que ce soit, nous identifions les meubles et objets qui ont une réelle valeur marchande. Un mobilier ancien, des bijoux, de l'argenterie ou une collection ne suivent pas le même circuit que le reste du logement : ils sont mis de côté pour être estimés, et leur montant vient en déduction du devis de débarras.",
      p2Before: "Cette étape est expliquée en détail sur notre page ",
      p2Link1: "rachat d'objets de valeur",
      p2Mid: ", et notre article ",
      p2Link2: "comment reconnaître un objet qui vaut la peine d'être racheté",
      p2After: " donne quelques repères simples si vous voulez vous faire une idée avant notre passage.",
    },
    s2: {
      title: "Réemploi et don : une seconde vie pour ce qui est encore utilisable",
      p1: "Pour les meubles et objets qui ne relèvent pas du rachat mais restent en bon état de fonctionnement, nous privilégions, dans la mesure du possible, une seconde vie plutôt qu'une mise en décharge directe. Un meuble simple mais solide, un électroménager qui fonctionne encore, une vaisselle complète n'ont pas vocation à finir systématiquement en déchet.",
      callout:
        "Le réemploi ne se décrète pas objet par objet à l'avance : il dépend de l'état réel constaté sur place le jour de l'intervention. C'est pourquoi nous ne promettons jamais qu'une pièce précise sera donnée plutôt que jetée, mais nous en tenons compte dans la mesure du raisonnable.",
      p2: "Cette étape ne remplace pas le rachat d'objets de valeur, elle vient après : un meuble déjà écarté par l'examen initial parce qu'il n'a pas de valeur marchande peut malgré tout avoir un intérêt en réemploi, s'il reste en état d'usage. Les deux circuits ne concernent donc pas les mêmes pièces et ne s'excluent pas l'un l'autre.",
    },
    s3: {
      title: "Le tri des matériaux pour ce qui ne peut pas être réemployé",
      p1: "Ce qui ne peut ni être racheté ni réemployé n'est pas mélangé en une seule benne. Nous séparons par type de matériau — bois, métal, textile, électroménager — plutôt que de tout regrouper indistinctement. Ce tri facilite le recyclage en aval et limite la part qui finit réellement en déchet ultime.",
      items: [
        "le bois, orienté vers les filières adaptées selon son état (massif, aggloméré, traité)",
        "le métal, valorisable presque systématiquement quel que soit son état",
        "le textile et les matelas, séparés du reste pour suivre leur propre filière",
        "l'électroménager, trié à part pour des raisons de sécurité et de recyclage des composants",
      ],
    },
    s4: {
      title: "Et la part qui ne peut vraiment plus être valorisée",
      p1: "Être transparent implique aussi de le dire clairement : tout ne se rachète pas, tout ne se réutilise pas et tout ne se recycle pas. Un objet cassé, incomplet ou trop dégradé pour l'une de ces filières suit alors la filière déchet classique, comme n'importe quel encombrant. Cette part existe, mais elle n'intervient qu'en dernier recours, une fois les trois étapes précédentes passées en revue.",
    },
    ideesRecues: {
      title: "Quelques idées reçues sur le devenir des objets",
      items: [
        { title: "« Tout finit dans la même benne »", text: "Non : le tri commence avant même le chargement, pas après. Ce qui peut être racheté, réemployé ou recyclé est identifié en amont, pas mélangé au reste." },
        { title: "« Le tri coûte plus cher »", text: "Le tri fait partie du déroulement normal d'un débarras. Il n'est pas facturé comme une option distincte du devis que vous recevez." },
        { title: "« Un objet abîmé n'intéresse personne »", text: "Un meuble fonctionnel mais marqué par le temps trouve encore preneur en réemploi, même s'il n'a pas de valeur de rachat au sens strict." },
      ],
    },
    s5: {
      title: "Pourquoi cette question mérite d'être posée",
      p1: "Vider le logement d'un proche ou une maison de famille amène souvent à se demander ce qu'il advient réellement des objets laissés derrière soi. C'est une question légitime, en particulier dans un contexte de succession où chaque meuble porte parfois une histoire. Nous préférons y répondre clairement plutôt que de la laisser sans réponse : le tri fait partie intégrante de notre façon de travailler, pas une option annexe ajoutée sur demande.",
      p2Before: "Ce principe reste identique quelle que soit la nature du chantier, qu'il s'agisse d'un ",
      p2Link1: "débarras d'appartement",
      p2Mid: " ou d'un ",
      p2Link2: "débarras de maison",
      p2After: " complète avec cave et grenier.",
    },
    s6: {
      title: "Bien préparer le tri de votre côté",
      p1: "Aucune préparation lourde n'est indispensable avant notre passage : le tri fait partie de notre travail, pas du vôtre. Quelques gestes simples facilitent toutefois l'organisation, surtout si vous n'êtes pas présent tout au long de l'intervention.",
      items: [
        "mettre de côté ce qui a une valeur sentimentale et que vous souhaitez garder vous-même",
        "signaler les meubles ou objets qui vous semblent anciens ou potentiellement de valeur",
        "regrouper si possible les pièces fragiles pour faciliter leur examen",
        "dégager l'accès aux zones à débarrasser — cave, grenier, dépendances — le jour de l'intervention",
      ],
      p2Before: "Notre article ",
      p2Link: "checklist avant un vide maison",
      p2After: " détaille ces points plus largement, au-delà de la seule question du devenir des objets traitée ici.",
    },
    ctaIntermediate: {
      title: "Un objet qui pourrait valoir la peine d'être racheté ?",
      text: "L'estimation se fait sur place, sans obligation d'accepter. Décrivez votre situation pour recevoir un devis gratuit intégrant, le cas échéant, la déduction du rachat.",
      link1: "Voir le rachat d'objets de valeur",
      link2: "Demander un devis gratuit",
    },
    s7: {
      title: "Ce qui influence le tri d'un logement à l'autre",
      p1: "La part exacte qui part au rachat, au réemploi ou au recyclage varie d'un logement à l'autre : elle dépend de ce qu'il contient réellement, pas d'un ratio fixe appliqué uniformément. Quelques facteurs reviennent toutefois d'un chantier à l'autre.",
      box1: "L'ancienneté et l'état général du mobilier, qui orientent vers le rachat, le réemploi ou directement le recyclage.",
      box2: "Le temps déjà consacré au tri par la famille avant notre passage, qui facilite l'identification des objets à part.",
      p2Before: "Ces éléments influencent aussi le prix de l'intervention, détaillé sur notre page ",
      p2Link: "tarifs",
      p2After: " : un devis gratuit reste la seule façon d'obtenir un chiffrage adapté à votre situation réelle.",
      p3: "Un logement occupé pendant plusieurs décennies contient en général davantage de pièces à examiner individuellement — pour le rachat comme pour le réemploi — qu'un appartement récemment habité. Ce n'est pas tant la taille du bien qui compte que la diversité de ce qu'il contient : cave, grenier et dépendances demandent souvent le plus de temps de tri.",
    },
    aRetenir: {
      title: "À retenir",
      items: [
        "Le tri se fait avant l'évacuation, jamais après : c'est ce qui permet de distinguer rachat, réemploi et recyclage.",
        "Les objets ayant une réelle valeur marchande sont mis de côté pour un rachat, déduit du devis.",
        "Ce qui est en bon état mais sans valeur de revente est orienté en priorité vers le réemploi plutôt que vers la benne.",
        "Le reste est trié par matériau (bois, métal, textile, électroménager) pour faciliter le recyclage en aval.",
        "Une part réduite, mais réelle, ne peut être ni réemployée ni recyclée et suit la filière déchet classique.",
      ],
    },
    faqTitle: "Questions fréquentes",
    faq: [
      { q: "Tout finit-il à la décharge après un débarras ?", a: "Non, c'est justement ce que le tri en amont permet d'éviter. Avant toute évacuation, les objets sont examinés pour distinguer ce qui peut être racheté, réemployé ou recyclé par matériau. Une part résiduelle, qui ne peut suivre aucun de ces circuits, part effectivement en déchet — mais elle n'est pas la majorité de ce qui sort d'un logement." },
      { q: "Puis-je récupérer un meuble après coup si j'ai changé d'avis ?", a: "Le mieux est de trancher avant notre intervention : une fois qu'un bien a quitté le logement, il n'est en général plus possible d'en récupérer un exemplaire précis, puisqu'il suit déjà son circuit (rachat, réemploi ou recyclage). C'est pour cette raison que le tri des objets à garder se fait toujours à votre rythme, avant l'évacuation." },
      { q: "Le tri des matériaux a-t-il un coût supplémentaire ?", a: "Non, il fait partie intégrante de notre façon de travailler et n'est pas facturé en plus du débarras. Séparer le bois, le métal, le textile et l'électroménager plutôt que tout mélanger prend un peu plus de temps sur place, mais cela reste inclus dans le devis initial." },
      { q: "Puis-je demander qu'un meuble précis soit donné plutôt que jeté ?", a: "Oui, vous pouvez signaler un meuble auquel vous tenez sans en avoir l'usage : nous en tenons compte dans la manière dont il est orienté, dans la mesure du possible selon son état. Ce n'est toutefois pas une garantie absolue pour chaque pièce prise isolément, l'objectif restant d'évacuer l'ensemble du logement dans le délai convenu." },
      { q: "Comment savoir si un meuble sera racheté, réemployé ou recyclé ?", a: "C'est l'examen effectué avant l'évacuation qui détermine son circuit : un objet à valeur marchande réelle part en rachat, un objet en bon état sans valeur de revente part en réemploi, et ce qui ne peut suivre aucun de ces deux chemins est trié par matériau pour le recyclage. Cette évaluation se fait au cas par cas, pas selon une règle générale par type de meuble." },
      { q: "Le réemploi concerne-t-il aussi l'électroménager ?", a: "Oui, dans la mesure où l'appareil fonctionne encore ou peut raisonnablement être remis en état. Un appareil hors d'usage suit en revanche une filière de recyclage adaptée à l'électroménager plutôt qu'un circuit de réemploi, pour des raisons de sécurité et de fiabilité." },
      { q: "Que faire des objets qui n'ont pas de valeur marchande mais une forte valeur sentimentale ?", a: "Ceux-là ne relèvent pas du tri que nous effectuons : ils vous appartiennent et le temps nécessaire pour les identifier et les mettre de côté est toujours respecté avant notre passage. Une fois ce tri personnel fait, seul le reste du logement suit le circuit décrit dans cet article." },
    ],
    ctaFinal: {
      title: "Une estimation gratuite pour votre vide maison à Bruxelles",
      text: "Décrivez-nous votre situation : nous vous expliquons, dès le devis, ce qui relève du rachat, du réemploi ou du recyclage pour votre logement.",
      seeZones: "Voir nos zones d'intervention à Bruxelles",
    },
  },
  nl: {
    h1: "Wat gebeurt er met uw meubels na een ontruiming?",
    heroText:
      "Een vraag die veel klanten zich stellen, vaak uit gehechtheid aan wat ze achterlaten. Hier leest u concreet, stap voor stap, wat er gebeurt tussen het moment waarop een meubelstuk de woning verlaat en zijn eindbestemming.",
    freeQuote: "Vraag een gratis offerte aan",
    intro:
      "Het leegmaken van een woning roept bijna altijd dezelfde vraag op: wat gebeurt er werkelijk met wat wordt afgevoerd? Het antwoord past in drie woorden — inkoop, hergebruik, recyclage — maar de volgorde waarin deze drie circuits optreden, telt evenveel als hun bestaan. Niets wordt zomaar geladen: elk meubelstuk en elk voorwerp wordt eerst bekeken, voordat het naar het ene of het andere traject wordt geleid. Deze gids beschrijft dat traject, van de eerste sortering ter plaatse tot het reële maar beperkte deel dat noch kan worden ingekocht, noch kan worden gevaloriseerd.",
    s1: {
      title: "Eerste sortering ter plaatse: wat kan worden ingekocht",
      p1: "Voordat we ook maar iets laden, identificeren we de meubels en voorwerpen met een reële marktwaarde. Antiek meubilair, juwelen, zilverwerk of een verzameling volgen niet hetzelfde traject als de rest van de woning: ze worden apart gehouden om te worden geschat, en het bedrag wordt in mindering gebracht op de offerte voor de ontruiming.",
      p2Before: "Deze stap wordt in detail uitgelegd op onze pagina ",
      p2Link1: "inkoop van waardevolle voorwerpen",
      p2Mid: ", en ons artikel ",
      p2Link2: "hoe herkent u een voorwerp dat de moeite waard is om in te kopen",
      p2After: " geeft enkele eenvoudige aanwijzingen als u zich vooraf al een idee wilt vormen.",
    },
    s2: {
      title: "Hergebruik en schenking: een tweede leven voor wat nog bruikbaar is",
      p1: "Voor meubels en voorwerpen die niet voor inkoop in aanmerking komen maar nog goed functioneren, geven wij, waar mogelijk, de voorkeur aan een tweede leven boven een rechtstreekse afvoer naar het stort. Een eenvoudig maar stevig meubelstuk, een nog werkend huishoudtoestel, een compleet servies zijn niet bestemd om systematisch als afval te eindigen.",
      callout:
        "Hergebruik kan niet vooraf per voorwerp worden beslist: het hangt af van de werkelijke staat die ter plaatse wordt vastgesteld op de dag van de interventie. Daarom beloven we nooit dat een specifiek stuk zal worden weggeschonken in plaats van weggegooid, maar we houden er wel rekening mee binnen redelijke grenzen.",
      p2: "Deze stap vervangt de inkoop van waardevolle voorwerpen niet, ze komt erna: een meubelstuk dat bij de eerste beoordeling al is afgewezen omdat het geen marktwaarde heeft, kan toch interessant zijn voor hergebruik, als het nog bruikbaar is. Beide circuits betreffen dus niet dezelfde stukken en sluiten elkaar niet uit.",
    },
    s3: {
      title: "De materiaalsortering voor wat niet kan worden hergebruikt",
      p1: "Wat noch kan worden ingekocht, noch hergebruikt, wordt niet in één en dezelfde container gegooid. We sorteren per materiaaltype — hout, metaal, textiel, huishoudtoestellen — in plaats van alles zonder onderscheid samen te voegen. Deze sortering vergemakkelijkt de recyclage stroomafwaarts en beperkt het deel dat werkelijk als restafval eindigt.",
      items: [
        "hout, doorgestuurd naar de geschikte verwerking naargelang de staat (massief, spaanplaat, behandeld)",
        "metaal, bijna altijd valoriseerbaar ongeacht de staat",
        "textiel en matrassen, apart gehouden van de rest om hun eigen traject te volgen",
        "huishoudtoestellen, apart gesorteerd om veiligheidsredenen en voor de recyclage van onderdelen",
      ],
    },
    s4: {
      title: "En het deel dat echt niet meer kan worden gevaloriseerd",
      p1: "Transparant zijn betekent ook duidelijk zeggen: niet alles kan worden ingekocht, niet alles kan worden hergebruikt en niet alles kan worden gerecycleerd. Een gebroken, onvolledig of te beschadigd voorwerp voor een van deze trajecten volgt dan de klassieke afvalstroom, zoals elk ander grofvuil. Dit deel bestaat, maar het komt pas als laatste optie aan bod, nadat de drie voorgaande stappen zijn doorlopen.",
    },
    ideesRecues: {
      title: "Enkele misvattingen over de bestemming van voorwerpen",
      items: [
        { title: "“Alles eindigt in dezelfde container”", text: "Niet waar: de sortering begint al vóór het laden, niet erna. Wat kan worden ingekocht, hergebruikt of gerecycleerd wordt vooraf geïdentificeerd, niet vermengd met de rest." },
        { title: "“Sorteren kost meer”", text: "Sorteren maakt deel uit van het normale verloop van een ontruiming. Het wordt niet als aparte optie bovenop de offerte gefactureerd die u ontvangt." },
        { title: "“Een beschadigd voorwerp interesseert niemand”", text: "Een functioneel maar door de tijd getekend meubelstuk vindt nog steeds een nieuwe bestemming via hergebruik, ook al heeft het strikt genomen geen inkoopwaarde." },
      ],
    },
    s5: {
      title: "Waarom deze vraag het waard is om gesteld te worden",
      p1: "Het leegmaken van de woning van een dierbare of een familiewoning brengt vaak de vraag met zich mee wat er werkelijk gebeurt met de achtergelaten voorwerpen. Dat is een legitieme vraag, zeker in een erfeniscontext waar elk meubelstuk soms een verhaal draagt. Wij beantwoorden die vraag liever duidelijk dan ze onbeantwoord te laten: sorteren maakt integraal deel uit van onze manier van werken, geen bijkomende optie die op aanvraag wordt toegevoegd.",
      p2Before: "Dit principe blijft hetzelfde, ongeacht de aard van de werf, of het nu gaat om een ",
      p2Link1: "appartement ontruimen",
      p2Mid: " of een volledig ",
      p2Link2: "huis leegmaken",
      p2After: " met kelder en zolder.",
    },
    s6: {
      title: "Sorteer uw spullen goed vóór onze komst",
      p1: "Geen zware voorbereiding is nodig vóór onze komst: het sorteren is ons werk, niet het uwe. Enkele eenvoudige gebaren vergemakkelijken echter de organisatie, vooral als u niet de hele tijd tijdens de interventie aanwezig bent.",
      items: [
        "leg apart wat sentimentele waarde heeft en wat u zelf wilt bewaren",
        "meld meubels of voorwerpen die u oud of mogelijk waardevol lijken",
        "groepeer indien mogelijk de breekbare stukken om hun beoordeling te vergemakkelijken",
        "maak de toegang tot de te ontruimen zones — kelder, zolder, bijgebouwen — vrij op de dag van de interventie",
      ],
      p2Before: "Ons artikel ",
      p2Link: "checklist vóór het leegmaken van een huis",
      p2After: " behandelt deze punten uitgebreider, verder dan alleen de hier besproken vraag over de bestemming van de voorwerpen.",
    },
    ctaIntermediate: {
      title: "Een voorwerp dat het misschien waard is om ingekocht te worden?",
      text: "De schatting gebeurt ter plaatse, zonder verplichting om te aanvaarden. Beschrijf uw situatie om een gratis offerte te ontvangen die, indien van toepassing, de aftrek van de inkoop bevat.",
      link1: "Bekijk de inkoop van waardevolle voorwerpen",
      link2: "Vraag een gratis offerte aan",
    },
    s7: {
      title: "Wat de sortering van de ene woning tot de andere beïnvloedt",
      p1: "Het exacte deel dat naar inkoop, hergebruik of recyclage gaat, verschilt van woning tot woning: het hangt af van wat er werkelijk in zit, niet van een vaste ratio die overal wordt toegepast. Toch komen enkele factoren steeds terug van werf tot werf.",
      box1: "De ouderdom en de algemene staat van het meubilair, die richting inkoop, hergebruik of rechtstreeks recyclage sturen.",
      box2: "De tijd die het gezin al aan het sorteren heeft besteed vóór onze komst, wat het identificeren van de apart te houden voorwerpen vergemakkelijkt.",
      p2Before: "Deze elementen beïnvloeden ook de prijs van de interventie, in detail toegelicht op onze pagina ",
      p2Link: "tarieven",
      p2After: ": een gratis offerte blijft de enige manier om een prijsopgave te krijgen die is afgestemd op uw werkelijke situatie.",
      p3: "Een woning die gedurende meerdere decennia bewoond is geweest, bevat over het algemeen meer stukken die individueel moeten worden beoordeeld — zowel voor inkoop als voor hergebruik — dan een recent bewoond appartement. Het is niet zozeer de grootte van het pand die telt, maar de diversiteit van wat het bevat: kelder, zolder en bijgebouwen vragen vaak de meeste sorteertijd.",
    },
    aRetenir: {
      title: "Om te onthouden",
      items: [
        "De sortering gebeurt vóór de afvoer, nooit erna: dat is wat het mogelijk maakt om inkoop, hergebruik en recyclage te onderscheiden.",
        "Voorwerpen met een reële marktwaarde worden apart gehouden voor inkoop, in mindering gebracht op de offerte.",
        "Wat in goede staat is maar geen wederverkoopwaarde heeft, gaat bij voorrang naar hergebruik in plaats van naar de container.",
        "De rest wordt gesorteerd per materiaal (hout, metaal, textiel, huishoudtoestellen) om de recyclage stroomafwaarts te vergemakkelijken.",
        "Een beperkt maar reëel deel kan noch worden hergebruikt, noch gerecycleerd en volgt de klassieke afvalstroom.",
      ],
    },
    faqTitle: "Veelgestelde vragen",
    faq: [
      { q: "Eindigt alles op het stort na een ontruiming?", a: "Nee, dat is nu net wat de voorafgaande sortering vermijdt. Vóór elke afvoer worden de voorwerpen onderzocht om te onderscheiden wat kan worden ingekocht, hergebruikt of per materiaal gerecycleerd. Een restdeel, dat geen van deze trajecten kan volgen, gaat inderdaad als afval weg — maar het vormt niet de meerderheid van wat uit een woning komt." },
      { q: "Kan ik achteraf een meubelstuk terugkrijgen als ik van gedachten ben veranderd?", a: "Het beste is om vooraf te beslissen, vóór onze interventie: eens een voorwerp de woning heeft verlaten, is het in het algemeen niet meer mogelijk om een specifiek exemplaar terug te krijgen, aangezien het al zijn traject volgt (inkoop, hergebruik of recyclage). Daarom gebeurt het sorteren van de te bewaren voorwerpen altijd op uw eigen tempo, vóór de afvoer." },
      { q: "Brengt de materiaalsortering extra kosten met zich mee?", a: "Nee, het maakt integraal deel uit van onze manier van werken en wordt niet extra gefactureerd bovenop de ontruiming. Hout, metaal, textiel en huishoudtoestellen apart houden in plaats van alles te mengen, vraagt wat meer tijd ter plaatse, maar dat blijft inbegrepen in de oorspronkelijke offerte." },
      { q: "Kan ik vragen dat een specifiek meubelstuk wordt weggeschonken in plaats van weggegooid?", a: "Ja, u kunt een meubelstuk aanduiden waaraan u gehecht bent zonder er zelf gebruik van te maken: we houden hiermee rekening in de manier waarop het wordt verwerkt, voor zover mogelijk gezien de staat ervan. Dit is echter geen absolute garantie voor elk afzonderlijk stuk, aangezien het doel blijft om de hele woning binnen de afgesproken termijn te ontruimen." },
      { q: "Hoe weet ik of een meubelstuk zal worden ingekocht, hergebruikt of gerecycleerd?", a: "Het is het onderzoek dat vóór de afvoer wordt uitgevoerd dat het traject bepaalt: een voorwerp met een reële marktwaarde gaat naar inkoop, een voorwerp in goede staat zonder wederverkoopwaarde gaat naar hergebruik, en wat geen van beide trajecten kan volgen wordt per materiaal gesorteerd voor recyclage. Deze beoordeling gebeurt geval per geval, niet volgens een algemene regel per type meubelstuk." },
      { q: "Geldt hergebruik ook voor huishoudtoestellen?", a: "Ja, voor zover het toestel nog werkt of redelijkerwijs kan worden hersteld. Een onbruikbaar toestel volgt daarentegen een recyclagetraject aangepast aan huishoudtoestellen in plaats van een hergebruikcircuit, om veiligheids- en betrouwbaarheidsredenen." },
      { q: "Wat met voorwerpen zonder marktwaarde maar met een sterke sentimentele waarde?", a: "Die vallen niet onder de sortering die wij uitvoeren: ze zijn van u, en de tijd die nodig is om ze te identificeren en apart te leggen, wordt altijd gerespecteerd vóór onze komst. Eens die persoonlijke sortering gebeurd is, volgt alleen de rest van de woning het in dit artikel beschreven traject." },
    ],
    ctaFinal: {
      title: "Een gratis schatting voor uw huis leegmaken in Brussel",
      text: "Beschrijf ons uw situatie: al bij de offerte leggen we u uit wat onder inkoop, hergebruik of recyclage valt voor uw woning.",
      seeZones: "Bekijk onze interventiezones in Brussel",
    },
  },
  en: {
    h1: "What happens to your furniture after a clearance?",
    heroText:
      "A question many clients ask themselves, often out of attachment to what they are leaving behind. Here is what actually happens, step by step, between the moment a piece of furniture leaves the property and its final destination.",
    freeQuote: "Request a free quote",
    intro:
      "Clearing out a home almost always raises the same question: what actually happens to what gets removed? The answer fits in three words — buyback, reuse, recycling — but the order in which these three paths come into play matters as much as their existence. Nothing is loaded at random: every piece of furniture and every item is looked at first, before being directed down one path rather than another. This guide details that journey, from the first on-site sorting to the real but limited share that can be neither bought back nor recovered.",
    s1: {
      title: "First sorting on site: what can be bought back",
      p1: "Before loading anything, we identify the furniture and items with real market value. Antique furniture, jewellery, silverware or a collection do not follow the same path as the rest of the home: they are set aside to be appraised, and their value is deducted from the clearance quote.",
      p2Before: "This step is explained in detail on our ",
      p2Link1: "item buyback",
      p2Mid: " page, and our article on ",
      p2Link2: "how to recognise an item worth buying back",
      p2After: " gives a few simple pointers if you'd like an idea before we arrive.",
    },
    s2: {
      title: "Reuse and donation: a second life for what is still usable",
      p1: "For furniture and items that are not bought back but remain in good working condition, we favour, wherever possible, a second life over direct disposal. A simple but sturdy piece of furniture, an appliance that still works, a complete set of tableware are not meant to systematically end up as waste.",
      callout:
        "Reuse cannot be decided item by item in advance: it depends on the actual condition found on site on the day of the job. That is why we never promise that a specific piece will be donated rather than thrown away, but we do take it into account within reason.",
      p2: "This step does not replace the buyback of valuable items, it comes after: a piece of furniture already ruled out by the initial assessment because it has no market value can still be of interest for reuse, if it remains usable. The two paths therefore do not concern the same pieces and are not mutually exclusive.",
    },
    s3: {
      title: "Sorting materials for what cannot be reused",
      p1: "What can neither be bought back nor reused is not mixed into a single skip. We separate by material type — wood, metal, textiles, appliances — rather than grouping everything together indiscriminately. This sorting makes downstream recycling easier and limits the share that truly ends up as final waste.",
      items: [
        "wood, directed to the appropriate stream depending on its condition (solid, chipboard, treated)",
        "metal, recoverable in almost all cases regardless of condition",
        "textiles and mattresses, separated from the rest to follow their own stream",
        "appliances, sorted separately for safety reasons and to recycle their components",
      ],
    },
    s4: {
      title: "And the share that truly cannot be recovered",
      p1: "Being transparent also means saying it clearly: not everything can be bought back, not everything can be reused, and not everything can be recycled. A broken, incomplete or too-degraded item for any of these paths then follows the standard waste stream, like any other bulky waste. This share does exist, but it only comes into play as a last resort, once the three previous steps have been reviewed.",
    },
    ideesRecues: {
      title: "A few misconceptions about what happens to your belongings",
      items: [
        { title: "“Everything ends up in the same skip”", text: "Not true: sorting starts before loading even begins, not after. What can be bought back, reused or recycled is identified upfront, not mixed with the rest." },
        { title: "“Sorting costs more”", text: "Sorting is part of the normal course of a clearance. It is not billed as a separate option on top of the quote you receive." },
        { title: "“A damaged item interests no one”", text: "A functional piece of furniture marked by time can still find a home through reuse, even if it has no buyback value in the strict sense." },
      ],
    },
    s5: {
      title: "Why this question deserves an answer",
      p1: "Clearing out a loved one's home or a family house often raises the question of what actually happens to the items left behind. It is a legitimate question, particularly in the context of an estate where every piece of furniture sometimes carries a story. We prefer to answer it clearly rather than leave it unanswered: sorting is an integral part of how we work, not an optional extra added on request.",
      p2Before: "This principle stays the same regardless of the type of job, whether it is an ",
      p2Link1: "apartment clearance",
      p2Mid: " or a full ",
      p2Link2: "house clearance",
      p2After: " including cellar and attic.",
    },
    s6: {
      title: "Preparing the sorting on your side",
      p1: "No major preparation is essential before we arrive: sorting is part of our job, not yours. A few simple steps nonetheless make things easier to organise, especially if you are not present throughout the job.",
      items: [
        "set aside anything with sentimental value that you want to keep yourself",
        "point out any furniture or items that seem old or potentially valuable",
        "group fragile pieces together where possible, to make them easier to assess",
        "clear access to the areas to be cleared — cellar, attic, outbuildings — on the day of the job",
      ],
      p2Before: "Our article on the ",
      p2Link: "checklist before a house clearance",
      p2After: " covers these points more broadly, beyond the single question of what happens to your belongings addressed here.",
    },
    ctaIntermediate: {
      title: "An item that might be worth buying back?",
      text: "The appraisal is done on site, with no obligation to accept. Describe your situation to receive a free quote that includes, where applicable, the buyback deduction.",
      link1: "See item buyback",
      link2: "Request a free quote",
    },
    s7: {
      title: "What influences sorting from one home to another",
      p1: "The exact share that goes to buyback, reuse or recycling varies from one home to another: it depends on what it actually contains, not on a fixed ratio applied uniformly. A few factors nonetheless recur from job to job.",
      box1: "The age and general condition of the furniture, which steer it towards buyback, reuse, or straight to recycling.",
      box2: "The time the family has already spent sorting before we arrive, which makes it easier to identify items to set aside.",
      p2Before: "These factors also affect the price of the job, detailed on our ",
      p2Link: "prices",
      p2After: " page: a free quote remains the only way to get pricing tailored to your actual situation.",
      p3: "A home occupied for several decades generally contains more pieces to assess individually — for buyback as well as reuse — than a recently occupied apartment. It is not so much the size of the property that matters as the diversity of what it contains: cellars, attics and outbuildings often require the most sorting time.",
    },
    aRetenir: {
      title: "Key takeaways",
      items: [
        "Sorting happens before removal, never after: this is what makes it possible to distinguish buyback, reuse and recycling.",
        "Items with real market value are set aside for buyback, deducted from the quote.",
        "What is in good condition but has no resale value is directed primarily towards reuse rather than the skip.",
        "The rest is sorted by material (wood, metal, textiles, appliances) to make downstream recycling easier.",
        "A small but real share can be neither reused nor recycled and follows the standard waste stream.",
      ],
    },
    faqTitle: "Frequently asked questions",
    faq: [
      { q: "Does everything end up at the landfill after a clearance?", a: "No, that is precisely what upfront sorting avoids. Before any removal, items are examined to distinguish what can be bought back, reused or recycled by material. A residual share, which cannot follow any of these paths, does end up as waste — but it is not the majority of what comes out of a home." },
      { q: "Can I get a piece of furniture back afterwards if I change my mind?", a: "It is best to decide before our job: once an item has left the property, it is generally no longer possible to retrieve a specific piece, as it is already following its path (buyback, reuse or recycling). This is why sorting what to keep always happens at your own pace, before removal." },
      { q: "Does sorting materials cost extra?", a: "No, it is an integral part of how we work and is not billed on top of the clearance. Separating wood, metal, textiles and appliances rather than mixing everything together takes a little more time on site, but this remains included in the initial quote." },
      { q: "Can I ask for a specific piece of furniture to be donated rather than thrown away?", a: "Yes, you can point out a piece of furniture you are attached to without having a use for it: we take this into account in how it is directed, as far as reasonably possible given its condition. This is not, however, an absolute guarantee for each individual piece, as the goal remains to clear the entire property within the agreed timeframe." },
      { q: "How do you know whether a piece of furniture will be bought back, reused or recycled?", a: "It is the assessment carried out before removal that determines its path: an item with real market value goes to buyback, an item in good condition with no resale value goes to reuse, and what can follow neither of these two paths is sorted by material for recycling. This assessment is done case by case, not according to a general rule per type of furniture." },
      { q: "Does reuse also apply to appliances?", a: "Yes, as long as the appliance still works or can reasonably be repaired. A non-working appliance, on the other hand, follows a recycling stream suited to appliances rather than a reuse path, for safety and reliability reasons." },
      { q: "What about items with no market value but strong sentimental value?", a: "Those are not part of the sorting we carry out: they belong to you, and the time needed to identify and set them aside is always respected before we arrive. Once that personal sorting is done, only the rest of the home follows the path described in this article." },
    ],
    ctaFinal: {
      title: "A free estimate for your house clearance in Brussels",
      text: "Tell us about your situation: from the quote stage, we explain what falls under buyback, reuse or recycling for your home.",
      seeZones: "See our service areas in Brussels",
    },
  },
};

const faqJsonLdFor = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
});

export default function PageClient({ locale }) {
  const c = CONTENT[locale];
  const faqJsonLd = faqJsonLdFor(c.faq);

  return (
    <article>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(900px 480px at 85% -10%, rgba(63,108,81,0.08), transparent 55%)" }}
        />
        <div className="relative mx-auto max-w-[900px] px-6 py-20 lg:px-10 lg:py-24">
          <h1 className="max-w-2xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroText}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.freeQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[820px] px-6 lg:px-10 space-y-14">
          {/* INTRO */}
          <div>
            <p className="leading-relaxed text-neutral-700">
              {c.intro}
            </p>
          </div>

          {/* ETAPE 1 : TRI / RACHAT */}
          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.s1.title}
            </h2>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s1.p1}
            </p>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s1.p2Before}
              <Link href="/rachat-objets" className="font-semibold text-accent hover:text-accent-dark">
                {c.s1.p2Link1}
              </Link>
              {c.s1.p2Mid}
              <Link href="/blog/reconnaitre-objet-valeur-rachat" className="font-semibold text-accent hover:text-accent-dark">
                {c.s1.p2Link2}
              </Link>
              {c.s1.p2After}
            </p>
          </div>

          {/* ETAPE 2 : REEMPLOI / DON */}
          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.s2.title}
            </h2>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s2.p1}
            </p>
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-sage/25 bg-sage/5 p-5">
              <ArrowPathIcon className="mt-0.5 h-5 w-5 shrink-0 text-sage" />
              <p className="text-sm leading-relaxed text-neutral-700">
                {c.s2.callout}
              </p>
            </div>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s2.p2}
            </p>
          </div>

          {/* ETAPE 3 : TRI MATERIAUX */}
          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.s3.title}
            </h2>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s3.p1}
            </p>
            <ul className="mt-6 space-y-2">
              {c.s3.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* ETAPE 4 : DECHET RESIDUEL */}
          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.s4.title}
            </h2>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s4.p1}
            </p>
          </div>

          {/* IDEES RECUES */}
          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.ideesRecues.title}
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {c.ideesRecues.items.map((item) => (
                <div key={item.title} className="rounded-2xl border border-sand-line bg-sand p-5">
                  <p className="font-bold text-ink">{item.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* POURQUOI CETTE QUESTION IMPORTE */}
          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.s5.title}
            </h2>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s5.p1}
            </p>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s5.p2Before}
              <Link href="/debarras-appartement" className="font-semibold text-accent hover:text-accent-dark">
                {c.s5.p2Link1}
              </Link>
              {c.s5.p2Mid}
              <Link href="/debarras-maison" className="font-semibold text-accent hover:text-accent-dark">
                {c.s5.p2Link2}
              </Link>
              {c.s5.p2After}
            </p>
          </div>

          {/* PREPARATION */}
          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.s6.title}
            </h2>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s6.p1}
            </p>
            <ul className="mt-6 space-y-2">
              {c.s6.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-6 leading-relaxed text-neutral-700">
              {c.s6.p2Before}
              <Link href="/blog/checklist-avant-vide-maison" className="font-semibold text-accent hover:text-accent-dark">
                {c.s6.p2Link}
              </Link>
              {c.s6.p2After}
            </p>
          </div>

          {/* CTA INTERMEDIAIRE */}
          <div className="rounded-[1.5rem] bg-brand-900 p-8 text-center sm:p-10">
            <GiftIcon className="mx-auto h-8 w-8 text-accent-light" />
            <h2 className="mt-4 font-serif text-2xl font-extrabold text-white">
              {c.ctaIntermediate.title}
            </h2>
            <p className="mx-auto mt-3 max-w-xl leading-relaxed text-white/75">
              {c.ctaIntermediate.text}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <CtaLink href="/rachat-objets" variant="accent" icon={ArrowRightIcon}>
                {c.ctaIntermediate.link1}
              </CtaLink>
              <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
                {c.ctaIntermediate.link2}
              </CtaLink>
            </div>
          </div>

          {/* CE QUI INFLUENCE LE CIRCUIT */}
          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">
              {c.s7.title}
            </h2>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s7.p1}
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3 rounded-2xl border border-sand-line bg-white p-5">
                <ArchiveBoxIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                <span className="text-sm leading-relaxed text-neutral-700">
                  {c.s7.box1}
                </span>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-sand-line bg-white p-5">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                <span className="text-sm leading-relaxed text-neutral-700">
                  {c.s7.box2}
                </span>
              </div>
            </div>
            <p className="mt-6 leading-relaxed text-neutral-700">
              {c.s7.p2Before}
              <Link href="/tarifs" className="font-semibold text-accent hover:text-accent-dark">
                {c.s7.p2Link}
              </Link>
              {c.s7.p2After}
            </p>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s7.p3}
            </p>
          </div>

          {/* A RETENIR */}
          <div className="rounded-[1.5rem] border border-brand-100 bg-brand-50 p-6 sm:p-8">
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.aRetenir.title}</h2>
            <ul className="mt-5 space-y-3">
              {c.aRetenir.items.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-cta-700" />
                  <span className="text-sm font-semibold leading-relaxed text-ink">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* FAQ */}
          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.faqTitle}</h2>
            <div className="mt-6 space-y-4">
              {c.faq.map((item) => (
                <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                  <p className="font-bold text-ink">{item.q}</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-[820px] px-6 text-center lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.ctaFinal.title}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-neutral-700">
            {c.ctaFinal.text}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.freeQuote}
            </CtaLink>
          </div>
          <div className="mt-6">
            <Link href="/vide-maison" className="inline-flex items-center gap-2 font-bold text-accent hover:text-accent-dark">
              {c.ctaFinal.seeZones}
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Contact />
    </article>
  );
}
